import { CrmType } from "./crm-settings";

export enum ClientRole {
  Client = "CLIENT",
  Assistant = "ASSISTANT",
  LegalAdvisor = "LEGAL_ADVISOR",
  InsuranceAdvisor = "INSURANCE_ADVISOR",
  TaxAdvisor = "TAX_ADVISOR",
  FinancialAdvisor = "FINANCIAL_ADVISOR",
  Other = "OTHER",
}

export const CLIENT_ROLE_LABELS: Record<ClientRole, string> = {
  [ClientRole.Client]: "Client",
  [ClientRole.Assistant]: "Assistant",
  [ClientRole.LegalAdvisor]: "Legal advisor",
  [ClientRole.InsuranceAdvisor]: "Insurance advisor",
  [ClientRole.TaxAdvisor]: "Tax advisor",
  [ClientRole.FinancialAdvisor]: "Financial advisor",
  [ClientRole.Other]: "Other",
};

export enum LookupType {
  PHONE = "PHONE",
  EMAIL = "EMAIL",
  SALESFORCE = "SALESFORCE",
}

export type ClientCrmContact = {
  crmId: string;
  crmType: CrmType;
  /**
   * The CRM record/object type this contact maps to (e.g. "contact" /
   * "account"). Drives the `recipientType` sent on CRM push so the backend
   * writes to the correct object type instead of falling back to a default.
   */
  entityType: string;
};

export enum ContactPhoneType {
  WORK = "WORK",
  HOME = "HOME",
  MOBILE = "MOBILE",
}

export const CONTACT_PHONE_TYPE_LABELS: Record<ContactPhoneType, string> = {
  [ContactPhoneType.WORK]: "Work",
  [ContactPhoneType.HOME]: "Home",
  [ContactPhoneType.MOBILE]: "Mobile",
};

/**
 * A single email address belonging to a client. Mirrors the backend
 * `EmailAddressResponseDto` exactly — only these fields cross the wire.
 */
export type EmailAddress = {
  id: string;
  email: string;
  primary: boolean;
};

/**
 * A single phone number belonging to a client. Mirrors the backend
 * `PhoneNumberResponseDto` exactly — only these fields cross the wire.
 */
export type PhoneNumber = {
  id: string;
  number: string;
  primary: boolean;
  type: ContactPhoneType;
  extension?: string;
};

export type Client = {
  id: string;
  firstname: string;
  lastname: string;
  phone: string;
  email: string;
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  userId: string;
  firmId: string;
  createdAt: number;
  updatedAt: number;
  deletedAt?: number | null;
  identifiers: ClientLookup[];
  crmContact?: ClientCrmContact;
  role?: ClientRole;
  emailAddresses?: EmailAddress[];
  phoneNumbers?: PhoneNumber[];
};

/**
 * Email entry accepted by the client create endpoint (no `id` — the row does
 * not exist yet). The first entry, or any flagged `primary`, becomes primary.
 */
export type ClientEmailInput = {
  email: string;
  primary?: boolean;
};

/**
 * Phone entry accepted by the client create endpoint (no `id`). `type` defaults
 * to HOME server-side; the first entry, or any flagged `primary`, becomes
 * primary.
 */
export type ClientPhoneInput = {
  number: string;
  type?: ContactPhoneType;
  extension?: string;
  primary?: boolean;
};

/**
 * Payload for creating or updating a client. Emails/phones are passed as input
 * arrays (id-less) — the backend persists them as child rows. Mirrors
 * focalmonolith's `ClientCreateRequestPayload` (which carries neither a
 * top-level `email` nor `phone`).
 */
export type ClientWritePayload = Partial<
  Omit<Client, "emailAddresses" | "phoneNumbers">
> & {
  emailAddresses?: ClientEmailInput[];
  phoneNumbers?: ClientPhoneInput[];
};

export type ClientLookup = {
  id: string;
  clientId: string;
  client: Client;
  type: LookupType;
  value: string;
};
